/**
 * Animated walkthrough of LLM inference: text is tokenized, embedded, pushed
 * through the layers (prefill), squeezed into one token, then fed back in one
 * token at a time (decode). Heavy math shows up as hot boxes, memory traffic
 * as dots in pipes, compression as dots smashing together.
 */
import { JetBrains_Mono, Silkscreen } from "next/font/google";
import { useCallback, useEffect, useRef, useState } from "react";
import { isDevelopment } from "~/config";
import { Sound } from "./audio";
import { H, MODEL_TOGGLE, Scene, W } from "./scene";
import { buildTimeline, type ModelKind } from "./timeline";

const pixelFont = Silkscreen({ weight: "400", subsets: ["latin"], display: "swap" });
const monoFont = JetBrains_Mono({ weight: "400", subsets: ["latin"], display: "swap" });

const TIMELINE = buildTimeline();

// Captions start at the same moments for every model, so an index is valid across all of them.
const captionAt = (t: number) => {
  let index = 0;
  TIMELINE.captions.moe.forEach((c, i) => {
    if (t >= c.t) index = i;
  });
  return index;
};

// The site styles every <button> with an !important gray background, and in dark mode that rule is `.dark button`,
// which outranks a single class. Every background here therefore gets a `dark:` twin (`.dark .class`) to win in both themes.
const BUTTON = "!shadow-none focus:ring-offset-0 focus-visible:ring-sky-400";
const ICON_BUTTON = `${BUTTON} p-1.5 rounded !bg-transparent hover:!bg-slate-800 dark:!bg-transparent dark:hover:!bg-slate-800 text-slate-300`;

const MUTED_KEY = "inference-animation:muted";
const VOLUME_KEY = "inference-animation:volume";
const VOLUME_STEPS = 10;
// The volume meter runs from cyan to fuchsia, like the scene's glow.
const volumeColor = (i: number) => `hsl(${199 + (i / (VOLUME_STEPS - 1)) * 93} 95% 65%)`;

const remember = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode: the choice just isn't remembered.
  }
};

const formatTime = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

const LEGEND = [
  { key: "heat", label: "Box heats up", detail: "heavy math" },
  { key: "pipe", label: "Dots along a pipe", detail: "data moving to or from memory" },
  { key: "smash", label: "Dots smash into one", detail: "information compressed" }
];

const LegendIcon = ({ kind }: { kind: string }) => {
  if (kind === "heat")
    return (
      <span className="inline-block w-5 h-4 rounded-sm border-2 border-orange-400 bg-orange-500/20 shadow-[0_0_10px_rgba(255,140,60,0.8)]" />
    );
  if (kind === "pipe")
    return (
      <span className="relative inline-block w-5 h-4">
        <span className="absolute inset-y-0 left-1/2 w-2 -translate-x-1/2 border-x border-sky-400/70" />
        <span className="absolute left-1/2 top-0.5 w-1 h-1 -translate-x-1/2 bg-sky-200 rounded-full shadow-[0_0_6px_#7dd3fc]" />
        <span className="absolute left-1/2 bottom-0.5 w-1 h-1 -translate-x-1/2 bg-sky-200 rounded-full shadow-[0_0_6px_#7dd3fc]" />
      </span>
    );
  return (
    <span className="relative inline-block w-5 h-4">
      <span className="absolute left-0 top-1.5 w-1 h-1 bg-fuchsia-300 rounded-full" />
      <span className="absolute right-0 top-0 w-1 h-1 bg-sky-300 rounded-full" />
      <span className="absolute left-1/2 top-1/2 w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full shadow-[0_0_8px_#fff]" />
    </span>
  );
};

export default function InferenceAnimation() {
  const figureRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const clockRef = useRef<HTMLSpanElement>(null);
  const sceneRef = useRef<Scene>(null);
  const soundRef = useRef<Sound>(null);
  const mutedRef = useRef(false);
  const volumeRef = useRef(VOLUME_STEPS);
  /** The volume unmuting brings back: the last one above zero that the listener settled on. */
  const lastVolumeRef = useRef(VOLUME_STEPS);
  const draggingRef = useRef(false);
  const meterRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<ModelKind>("moe");
  const tRef = useRef(0);
  const playingRef = useRef(false);
  const speedRef = useRef(1);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [caption, setCaption] = useState(0);
  const [model, setModel] = useState<ModelKind>("moe");
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(VOLUME_STEPS);
  const [adjusting, setAdjusting] = useState(false);

  // Browsers only allow audio after a click or key press, so the engine is created by the first one.
  const sound = useCallback(() => {
    if (mutedRef.current) return null;
    if (!soundRef.current) {
      soundRef.current = new Sound(TIMELINE, modelRef.current);
      soundRef.current.setVolume(volumeRef.current / VOLUME_STEPS);
    }
    soundRef.current.unlock();
    return soundRef.current;
  }, []);

  const chooseModel = (next: ModelKind) => {
    sceneRef.current?.setModel(next);
    modelRef.current = next;
    setModel(next);
    const s = sound();
    s?.setModel(next);
    s?.ui("select", next);
  };
  const hoverModel = (hovered: ModelKind | null) => {
    if (sceneRef.current) sceneRef.current.hover = hovered;
    if (hovered && hovered !== modelRef.current) soundRef.current?.ui("hover");
  };

  const setMutedTo = (next: boolean) => {
    mutedRef.current = next;
    setMuted(next);
    remember(MUTED_KEY, next ? "1" : "0");
    soundRef.current?.setEnabled(!next);
  };

  const storeVolume = (level: number) => {
    volumeRef.current = level;
    // A drag down to zero passes through every level on the way, so it only counts where the drag ends.
    if (level > 0 && !draggingRef.current) lastVolumeRef.current = level;
    setVolume(level);
    remember(VOLUME_KEY, String(level));
    soundRef.current?.setVolume(level / VOLUME_STEPS);
  };

  const toggleMute = () => {
    const unmuting = mutedRef.current;
    // Unmuting at zero volume would still be silent, so it brings back the last level.
    if (unmuting && volumeRef.current === 0) storeVolume(lastVolumeRef.current);
    setMutedTo(!unmuting);
    if (unmuting) sound()?.ui("select");
  };

  const changeVolume = (level: number) => {
    const next = Math.round(Math.min(VOLUME_STEPS, Math.max(0, level)));
    if (next === (mutedRef.current ? 0 : volumeRef.current)) return;
    storeVolume(next);
    // As in most players, sliding to zero mutes and turning it back up unmutes.
    if ((next === 0) !== mutedRef.current) setMutedTo(next === 0);
    if (next > 0) sound()?.volumeTick(next / VOLUME_STEPS);
  };

  const volumeFromPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = meterRef.current.getBoundingClientRect();
    changeVolume(Math.ceil(((e.clientX - r.left) / r.width) * VOLUME_STEPS));
  };

  const dragVolume = (dragging: boolean) => {
    draggingRef.current = dragging;
    setAdjusting(dragging);
    if (!dragging && volumeRef.current > 0) lastVolumeRef.current = volumeRef.current;
  };

  const onVolumeKey = (e: React.KeyboardEvent) => {
    const now = mutedRef.current ? 0 : volumeRef.current;
    const moves: Record<string, number> = {
      ArrowRight: now + 1,
      ArrowUp: now + 1,
      ArrowLeft: now - 1,
      ArrowDown: now - 1,
      PageUp: now + 3,
      PageDown: now - 3,
      Home: 0,
      End: VOLUME_STEPS
    };
    if (!(e.key in moves)) return;
    e.preventDefault();
    changeVolume(moves[e.key]);
  };

  const seek = useCallback((t: number) => {
    const next = Math.min(TIMELINE.duration, Math.max(0, t));
    tRef.current = next;
    sceneRef.current?.seek(next);
    soundRef.current?.seek(next);
    setEnded(next >= TIMELINE.duration);
  }, []);

  const play = useCallback(() => {
    sound();
    if (tRef.current >= TIMELINE.duration) seek(0);
    playingRef.current = true;
    setPlaying(true);
    setStarted(true);
    setEnded(false);
  }, [seek, sound]);

  const insertCoin = () => {
    sound()?.ui("coin");
    play();
  };

  const pause = useCallback(() => {
    playingRef.current = false;
    setPlaying(false);
  }, []);

  const toggle = useCallback(() => (playingRef.current ? pause() : play()), [pause, play]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(MUTED_KEY) === "1";
      mutedRef.current = saved;
      setMuted(saved);
      const stored = localStorage.getItem(VOLUME_KEY);
      const level = stored === null ? NaN : Number(stored);
      if (Number.isInteger(level) && level >= 0 && level <= VOLUME_STEPS) {
        volumeRef.current = level;
        if (level > 0) lastVolumeRef.current = level;
        setVolume(level);
      }
    } catch {
      // No storage: sound stays on, at full volume.
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const scene = new Scene(canvas, TIMELINE);
    sceneRef.current = scene;
    scene.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const fonts = { pixel: pixelFont.style.fontFamily, mono: monoFont.style.fontFamily };
    scene.setFonts(fonts);
    Promise.all([document.fonts.load(`12px ${fonts.pixel}`), document.fonts.load(`20px ${fonts.mono}`)])
      .catch(() => undefined)
      .then(() => scene.setFonts(fonts));

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      scene.resize(r.width, r.height, Math.min(2, window.devicePixelRatio || 1));
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    let raf = 0;
    let last = performance.now();
    let ambient = 0;
    let visible = true;
    let shownCaption = -1;
    const frame = (now: number) => {
      // Off screen: stop until the observer brings the canvas back (and keep a stray frame from restarting the sound).
      if (!visible) {
        raf = 0;
        return;
      }
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      ambient += dt;
      let dtSim = 0;
      if (playingRef.current) {
        dtSim = dt * speedRef.current;
        tRef.current = Math.min(TIMELINE.duration, tRef.current + dtSim);
        if (tRef.current >= TIMELINE.duration) {
          playingRef.current = false;
          setPlaying(false);
          setEnded(true);
        }
      }
      scene.render(tRef.current, dtSim, ambient);
      try {
        soundRef.current?.update(tRef.current, playingRef.current, speedRef.current);
      } catch (error) {
        // Sound is a bonus: if the audio engine fails, turn it off and keep the picture running.
        console.error(error);
        soundRef.current?.dispose();
        soundRef.current = null;
        mutedRef.current = true;
        setMuted(true);
      }
      if (progressRef.current) progressRef.current.style.width = `${(tRef.current / TIMELINE.duration) * 100}%`;
      const clock = `${formatTime(tRef.current)} / ${formatTime(TIMELINE.duration)}`;
      if (clockRef.current && clockRef.current.textContent !== clock) clockRef.current.textContent = clock;
      const c = captionAt(tRef.current);
      if (c !== shownCaption) {
        shownCaption = c;
        setCaption(c);
      }
      raf = visible ? requestAnimationFrame(frame) : 0;
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (!visible) soundRef.current?.hush();
      if (visible && !raf) {
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    });
    io.observe(canvas);
    // Hidden tabs stop drawing frames, so the sound stops with them.
    const onVisibility = () => document.hidden && soundRef.current?.hush();
    document.addEventListener("visibilitychange", onVisibility);
    raf = requestAnimationFrame(frame);

    if (isDevelopment) {
      (window as unknown as Record<string, unknown>).__inferenceAnimation = {
        seek,
        play,
        pause,
        time: () => tRef.current,
        timeline: TIMELINE,
        sound: () => soundRef.current,
        renderAudio: (from: number, to: number, speed = 1, kind: ModelKind = "moe") =>
          Sound.render(TIMELINE, kind, from, to, speed)
      };
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      soundRef.current?.dispose();
      soundRef.current = null;
    };
  }, [seek, play, pause]);

  const seekFromPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = barRef.current.getBoundingClientRect();
    seek(((e.clientX - r.left) / r.width) * TIMELINE.duration);
  };

  const toggleSpeed = () => {
    const next = speed === 1 ? 0.5 : 1;
    speedRef.current = next;
    setSpeed(next);
    sound()?.ui(next < 1 ? "slow" : "fast");
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else figureRef.current?.requestFullscreen?.();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === " " || e.key === "k") {
      e.preventDefault();
      toggle();
    } else if (e.key === "ArrowRight") seek(tRef.current + 5);
    else if (e.key === "ArrowLeft") seek(tRef.current - 5);
    else if (e.key === "m") toggleMute();
  };

  const current = TIMELINE.captions[model][caption];
  const shownVolume = muted ? 0 : volume;
  // The volume meter shows while the sound button is hovered or keyboard-focused, and stays up through a drag. Its
  // backing only fades once the bars have pulled back in.
  const meterShown = adjusting
    ? ""
    : "opacity-0 pointer-events-none delay-300 group-hover:opacity-100 group-hover:pointer-events-auto group-hover:delay-0 group-has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-has-[:focus-visible]:delay-0";
  // Each bar waits its turn: --in on the way out of the speaker, --out on the way back in.
  const barShown = adjusting
    ? "[--d:0ms]"
    : "opacity-0 translate-x-1 [--d:var(--out)] group-hover:opacity-100 group-hover:translate-x-0 group-hover:[--d:var(--in)] group-has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:translate-x-0 group-has-[:focus-visible]:[--d:var(--in)]";

  return (
    <figure
      ref={figureRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      aria-label="Animation: how a language model turns a prompt into its next tokens"
      className="not-prose my-10 relative left-1/2 -translate-x-1/2 w-[min(calc(100vw-2rem),56rem)] outline-none focus-visible:ring-2 focus-visible:ring-sky-400 rounded-xl [&:fullscreen]:w-full [&:fullscreen]:left-0 [&:fullscreen]:translate-x-0 [&:fullscreen]:flex [&:fullscreen]:flex-col [&:fullscreen]:justify-center [&:fullscreen]:bg-black [&:fullscreen]:rounded-none"
    >
      <div className="overflow-hidden rounded-xl border border-slate-700/80 bg-[#03050c] shadow-[0_0_40px_rgba(56,120,255,0.18)] [:fullscreen_&]:rounded-none [:fullscreen_&]:border-0 [:fullscreen_&]:w-full [:fullscreen_&]:max-w-[calc((100vh-11rem)*16/9)] [:fullscreen_&]:mx-auto">
        <div className="relative aspect-video">
          <canvas
            ref={canvasRef}
            onClick={toggle}
            role="img"
            aria-label="Tokens shown as glowing dots flow from the prompt through an embedding table, ninety layers and an output head, while pipes carry weights from GPU memory."
            className="absolute inset-0 w-full h-full cursor-pointer"
          />
          <div role="group" aria-label="Model architecture">
            {MODEL_TOGGLE.options.map((o) => (
              <button
                key={o.model}
                type="button"
                aria-pressed={model === o.model}
                aria-label={o.name}
                title={o.name}
                onClick={() => chooseModel(o.model)}
                onPointerEnter={() => hoverModel(o.model)}
                onPointerLeave={() => hoverModel(null)}
                onFocus={() => hoverModel(o.model)}
                onBlur={() => hoverModel(null)}
                className={`${BUTTON} absolute z-10 !rounded !bg-transparent hover:!bg-transparent dark:!bg-transparent dark:hover:!bg-transparent focus:!ring-0 focus-visible:!ring-2 cursor-pointer`}
                style={{
                  left: `${(o.x / W) * 100}%`,
                  top: `${(MODEL_TOGGLE.y / H) * 100}%`,
                  width: `${(o.w / W) * 100}%`,
                  height: `${(MODEL_TOGGLE.h / H) * 100}%`
                }}
              />
            ))}
          </div>
          {!started && (
            <button
              type="button"
              onClick={insertCoin}
              className={`${BUTTON} absolute inset-x-0 bottom-0 top-[7%] flex flex-col items-center justify-center gap-3 !rounded-none !bg-[#02040c]/55 hover:!bg-[#02040c]/35 dark:!bg-[#02040c]/55 dark:hover:!bg-[#02040c]/35 text-sky-200 transition-colors`}
            >
              <span className="flex items-center justify-center w-16 h-16 rounded-full border-2 border-sky-300/80 shadow-[0_0_24px_rgba(125,211,252,0.6)]">
                <svg viewBox="0 0 24 24" className="w-7 h-7 ml-1 fill-current" aria-hidden="true">
                  <path d="M6 4l14 8-14 8z" />
                </svg>
              </span>
              <span className={`${pixelFont.className} text-sm sm:text-base tracking-widest animate-pulse`}>
                PRESS START
              </span>
              <span className={`${pixelFont.className} text-[10px] tracking-widest text-sky-300/70`}>
                {muted ? "SOUND OFF" : "SOUND ON"}
              </span>
            </button>
          )}
          {ended && (
            <button
              type="button"
              onClick={insertCoin}
              className={`${pixelFont.className} ${BUTTON} absolute left-1/2 bottom-[8%] -translate-x-1/2 px-4 py-2 rounded-md border border-sky-300/80 !bg-[#02040c]/85 hover:!bg-sky-900 dark:!bg-[#02040c]/85 dark:hover:!bg-sky-900 text-sky-200 text-xs sm:text-sm tracking-widest`}
            >
              ↻ REPLAY
            </button>
          )}
        </div>

        <div className="px-3 pt-3 pb-2 bg-[#060a16] border-t border-slate-800">
          <div
            ref={barRef}
            onPointerDown={(e) => (e.currentTarget.setPointerCapture(e.pointerId), seekFromPointer(e))}
            onPointerMove={(e) => e.buttons === 1 && seekFromPointer(e)}
            className="relative h-2 rounded-full bg-slate-800 cursor-pointer touch-none"
            role="presentation"
          >
            <div
              ref={progressRef}
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-sky-500 to-cyan-300 shadow-[0_0_10px_rgba(103,232,249,0.7)]"
            />
            {TIMELINE.chapters.slice(1).map((c) => (
              <span
                key={c.label}
                className="absolute top-1/2 w-1 h-3 -translate-y-1/2 -translate-x-1/2 rounded-sm bg-slate-400/70"
                style={{ left: `${(c.t / TIMELINE.duration) * 100}%` }}
              />
            ))}
          </div>

          <div className="mt-2 flex items-center gap-2 text-slate-300">
            <button type="button" onClick={toggle} aria-label={playing ? "Pause" : "Play"} className={ICON_BUTTON}>
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                {playing ? <path d="M6 4h4v16H6zM14 4h4v16h-4z" /> : <path d="M6 4l14 8-14 8z" />}
              </svg>
            </button>
            <button
              type="button"
              onClick={() => {
                seek(0);
                play();
              }}
              aria-label="Restart"
              className={ICON_BUTTON}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-current stroke-2" aria-hidden="true">
                <path d="M4 12a8 8 0 1 0 2.3-5.7M4 4v5h5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span ref={clockRef} className={`${pixelFont.className} text-[11px] text-slate-400 tabular-nums`} />
            <div className="ml-auto flex items-center gap-1">
              {/* Hovering the sound button (or tabbing to it) brings up a volume meter beside it. */}
              <div className="group relative flex items-center">
                {/*
                  The backing matches the control bar, so it only shows where the meter covers the clock on a narrow
                  screen. The right padding is the gap to the button: inside the meter, so crossing it keeps the hover.
                */}
                <div
                  className={`absolute right-full top-1/2 z-10 -translate-y-1/2 pl-1 pr-2 bg-[#060a16] transition-opacity duration-100 ${meterShown}`}
                >
                  <div
                    ref={meterRef}
                    role="slider"
                    tabIndex={0}
                    aria-label="Volume"
                    aria-valuemin={0}
                    aria-valuemax={VOLUME_STEPS}
                    aria-valuenow={shownVolume}
                    aria-valuetext={shownVolume ? `${shownVolume * 10}%` : "Muted"}
                    onPointerDown={(e) => {
                      e.currentTarget.setPointerCapture(e.pointerId);
                      dragVolume(true);
                      volumeFromPointer(e);
                    }}
                    onPointerMove={(e) => e.buttons === 1 && volumeFromPointer(e)}
                    onPointerUp={() => dragVolume(false)}
                    onPointerCancel={() => dragVolume(false)}
                    onKeyDown={onVolumeKey}
                    className="flex items-end gap-[2px] h-6 py-1 px-0.5 cursor-pointer touch-none rounded-sm outline-none focus-visible:ring-1 focus-visible:ring-sky-400"
                  >
                    {Array.from({ length: VOLUME_STEPS }, (_, i) => (
                      // Bars light up outward from the speaker, and on the way out the farthest goes first.
                      <span
                        key={i}
                        className={`w-[5px] rounded-[1px] ${barShown}`}
                        style={
                          {
                            height: `${30 + i * 7.5}%`,
                            background: i < shownVolume ? volumeColor(i) : "#1e293b",
                            boxShadow: i < shownVolume ? `0 0 6px ${volumeColor(i)}` : "none",
                            "--in": `${(VOLUME_STEPS - 1 - i) * 15}ms`,
                            "--out": `${i * 15}ms`,
                            transitionProperty: "opacity, transform, background-color, box-shadow",
                            transitionDuration: "150ms, 150ms, 75ms, 75ms",
                            transitionDelay: "var(--d), var(--d), 0ms, 0ms"
                          } as React.CSSProperties
                        }
                      />
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label="Sound"
                  aria-pressed={!muted}
                  title="Sound (M)"
                  className={ICON_BUTTON}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-5 h-5 fill-none stroke-current stroke-2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4 9v6h4l5 4V5L8 9H4z" className="fill-current" />
                    {muted ? (
                      <path d="M16 9.5l5 5M21 9.5l-5 5" />
                    ) : (
                      <path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" />
                    )}
                  </svg>
                </button>
              </div>
              <button
                type="button"
                onClick={toggleSpeed}
                className={`${pixelFont.className} ${BUTTON} px-2 py-1 rounded text-[11px] !bg-transparent hover:!bg-slate-800 dark:!bg-transparent dark:hover:!bg-slate-800 ${speed !== 1 ? "text-amber-300" : "text-slate-400"}`}
                aria-label="Toggle slow motion"
              >
                {speed === 1 ? "1X" : "0.5X"}
              </button>
              <button type="button" onClick={toggleFullscreen} aria-label="Fullscreen" className={ICON_BUTTON}>
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-current stroke-2" aria-hidden="true">
                  <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          <div className="mt-1 flex flex-wrap gap-1.5">
            {TIMELINE.chapters.map((c) => (
              <button
                key={c.label}
                type="button"
                onClick={() => {
                  seek(c.t);
                  play();
                }}
                className={`${BUTTON} px-2 py-0.5 !rounded-full border text-xs transition-colors ${
                  current.chapter === c.label
                    ? "border-cyan-300 text-cyan-200 !bg-cyan-400/10 dark:!bg-cyan-400/10"
                    : "border-slate-700 text-slate-400 !bg-transparent hover:text-slate-200 hover:border-slate-500 hover:!bg-slate-800/60 dark:!bg-transparent dark:hover:!bg-slate-800/60"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <p className="mt-3 mb-1 min-h-[4.5rem] text-[15px] leading-relaxed text-slate-200" aria-live="polite">
            <span className="font-semibold text-cyan-300">{current.chapter}. </span>
            {current.text}
          </p>

          <ul className="mt-2 mb-1 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-slate-400">
            {LEGEND.map((l) => (
              <li key={l.key} className="flex items-center gap-2">
                <LegendIcon kind={l.key} />
                <span>
                  <span className="text-slate-200">{l.label}</span>: {l.detail}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <figcaption className="mt-2 px-2 text-center text-sm text-gray-600 dark:text-gray-400 [:fullscreen_&]:text-gray-400">
        Token IDs are real (GPT-4&rsquo;s tokenizer); word scores, attention strengths and expert choices are
        illustrative. Real chips run every layer on the same compute units.
      </figcaption>
    </figure>
  );
}
